import {
  create_company_cases_state,
  get_entity_type_name,
  is_open_case_status,
  normalize_key,
  read_session_current_role,
  read_session_project_id,
} from '../../CompanyAdmin/CompanyCases/company_cases_helpers';
import { can_route_case } from '../../CompanyAdmin/CompanyCases/company_cases_permissions';
import { company_cases_loading_methods } from '../../CompanyAdmin/CompanyCases/company_cases_loading';
import { company_cases_creation_methods } from '../../CompanyAdmin/CompanyCases/company_cases_creation';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  assign_requestor_application_users,
  create_requestor_case,
  fetch_reassignable_users,
  fetch_requestor_application_forms,
  update_requestor_application_status,
} from '@/services/requestor_portal_service';
import { fetch_assign_form_status_data } from '@/services/workflow_service';
import {
  created_manual_case,
  resolve_manual_case_form_ids,
  validate_manual_case_registration,
} from '../manual_case_form_helpers';

const INFO_CALLOUT = 'X-VERDICT ships as a generic case engine. Categories, role labels and workflows are per-tenant configuration on the same shell.';
const HANDLER_ROLE_TOKENS = ['case handler', 'case owner', 'reviewer', 'owner'];

function manual_case_form() {
  return {
    title: 'Register · inbound request',
    subtitle: 'Portal · Just now',
    description: '',
    requester: '',
    contact: '',
    location: '',
    case_type: '',
  };
}

function case_detail_query(case_item = {}) {
  return Object.fromEntries([
    ['application_id', case_item?.application_id],
    ['entity_profile_id', case_item?.entity_profile_id],
    ['entity_type_id', case_item?.entity_type_id],
  ].filter(([, value]) => Boolean(value)));
}

function sla_state_text(case_item = {}) {
  return normalize_key([
    case_item?.sla,
    case_item?.sla_class,
    case_item?.sla_runtime?.status,
    case_item?.sla_runtime?.status_label,
    case_item?.sla_runtime?.badge_label,
    case_item?.due_status,
  ].filter(Boolean).join(' '));
}

function has_sla_breach(case_item = {}) {
  const state = sla_state_text(case_item);
  return ['overdue', 'breach', 'breached', 'escalated', 'danger'].some((flag) => state.includes(flag));
}

function has_sla_warning(case_item = {}) {
  const state = sla_state_text(case_item);
  return ['warning', 'at-risk', 'text-warning', 'overdue', 'breach', 'breached', 'escalated', 'danger']
    .some((flag) => state.includes(normalize_key(flag)));
}

function normalized_text(value = '') {
  return String(value || '').trim().toLowerCase();
}

function is_email(value = '') {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function unique_by(items = [], key_builder = (item) => item) {
  const seen = new Set();
  return items.filter((item) => {
    const key = key_builder(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function workflow_status_option(status = {}) {
  const id = Number(status?.id || status?.status_id || status?.assigned_form_status_id);
  const label = String(status?.status || status?.status_name || status?.label || status?.name || '').trim();
  const order = Number(status?.order_number ?? status?.order ?? status?.priority ?? 0);
  const is_final = Boolean(status?.is_final);
  return id && label ? { id, label, order, is_final } : null;
}

function assigned_statuses_from_response(response = {}) {
  return response?.assigned_statuses
    || response?.data?.assigned_statuses
    || response?.selected_statuses
    || response?.data?.selected_statuses
    || [];
}

function sorted_unique_statuses(statuses = []) {
  return unique_by(statuses, (status) => String(status?.id))
    .sort((left, right) => Number(left?.order || 0) - Number(right?.order || 0));
}

function role_text(user = {}) {
  return normalized_text(
    user?.user?.role?.role
    || user?.role_name
    || user?.role
    || user?.user?.role_name
  );
}

function role_id(user = {}) {
  return Number(
    user?.user?.role?.id
    || user?.role_id
    || user?.user?.role_id
  ) || null;
}

function group_id(user = {}) {
  return Number(
    user?.user?.role?.group_id
    || user?.group_id
    || user?.user?.group_id
    || user?.role_group_id
  ) || null;
}

function user_name(user = {}) {
  const full_name = [
    user?.user?.first_name,
    user?.user?.last_name,
  ].filter(Boolean).join(' ').trim();

  return full_name || String(
    user?.user?.email
    || user?.user?.username
    || user?.name
    || user?.email
    || ''
  ).trim();
}

function is_case_handler_role(role = '') {
  const normalized_role = normalized_text(role);
  return HANDLER_ROLE_TOKENS.some((token) => normalized_role.includes(token));
}

function assignable_case_handler(user = {}) {
  const user_id = Number(user?.user_id || user?.id || user?.user?.id) || null;
  const name = user_name(user);
  const role = role_text(user);
  const assigned_role_id = role_id(user);
  const panel_group_id = group_id(user);

  if (!user_id || !name || !panel_group_id || !assigned_role_id) return null;
  if (role.includes('system admin')) return null;
  if (!is_case_handler_role(role)) return null;

  return {
    user_id,
    name,
    role: String(role || '').trim(),
    role_id: assigned_role_id,
    group_id: panel_group_id,
  };
}

function selected_case_records(cases = [], selected_ids = []) {
  const selected_id_set = new Set(selected_ids);
  return cases.filter((case_item) => selected_id_set.has(case_item.case_code));
}

async function final_workflow_status_for_case(case_item = {}) {
  if (!case_item?.application_id) return null;

  const application_forms = await fetch_requestor_application_forms(case_item.application_id);
  const form_ids = [...new Set(
    application_forms
      .map((application_form) => Number(application_form?.form?.id || application_form?.form_id || application_form?.id))
      .filter(Boolean)
  )];

  const status_groups = await Promise.all(
    form_ids.map((form_id) => fetch_assign_form_status_data({ form_id }))
  );

  const statuses = sorted_unique_statuses(
    status_groups
      .flatMap((response) => assigned_statuses_from_response(response))
      .map(workflow_status_option)
      .filter(Boolean)
  );

  return statuses.find((status) => status.is_final) || statuses[statuses.length - 1] || null;
}

export default {
  name: 'RegisteredCases',
  components: {
    BlockingLoaderOverlay,
  },
  mixins: [alert_mixin],
  data: () => ({
    ...create_company_cases_state(),
    info_callout: INFO_CALLOUT,
    search_term: '',
    selected_case_ids: [],
    manual_case: manual_case_form(),
    submitting_manual_case: false,
    show_assign_modal: false,
    assignable_users: [],
    selected_assign_user_id: '',
    loading_assignable_users: false,
    assign_error: '',
    submitting_assign_cases: false,
    show_close_modal: false,
    close_error: '',
    closing_cases: false,
  }),
  computed: {
    project_id() {
      return this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id();
    },
    current_role() {
      return this.$store?.getters?.currentRole || read_session_current_role();
    },
    should_open_new_case() {
      return String(this.$route?.query?.open_new_case || '') === '1';
    },
    metrics() {
      const open_case_count = this.cases.filter((case_item) => is_open_case_status(case_item?.status)).length;
      const breached_count = this.cases.filter(has_sla_breach).length;
      const warning_count = this.cases.filter(has_sla_warning).length;

      return [
        { label: 'OPEN CASES', value: String(open_case_count), meta: this.loading_cases ? 'Loading live queue' : 'Live queue', meta_class: '' },
        { label: 'SLA WARNING', value: String(warning_count), meta: '<= 48hr to breach', meta_class: '' },
        { label: 'SLA BREACHED', value: String(breached_count), meta: breached_count ? 'Action required' : 'None', meta_class: '' },
        { label: 'AVG. RESOLUTION', value: '-', meta: 'Cases', meta_class: '' },
      ];
    },
    filters() {
      const entity_type_filters = this.case_types
        .map((entity_type) => {
          const label = get_entity_type_name(entity_type);
          return label
            ? { key: normalize_key(label), label }
            : null;
        })
        .filter(Boolean);

      return [
        { key: 'all', label: 'All domains' },
        ...entity_type_filters,
        { key: 'high-risk', label: 'High Risk' },
        { key: 'sla-breached', label: 'SLA Breached' },
      ];
    },
    filtered_cases() {
      const search_value = normalize_key(this.search_term);
      let visible_cases = this.cases;

      if (this.active_filter === 'high-risk') {
        visible_cases = this.cases.filter((item) => item.risk_label === 'High');
      } else if (this.active_filter === 'sla-breached') {
        visible_cases = this.cases.filter(has_sla_breach);
      } else if (this.active_filter !== 'all') {
        visible_cases = this.cases.filter((item) => item.domain_key === this.active_filter);
      }

      if (!search_value) {
        return visible_cases;
      }

      return visible_cases.filter((item) => [
        item.case_code,
        item.title,
        item.subtitle,
        item.domain,
        item.owner,
        item.status,
        item.sla,
      ].some((value) => normalize_key(value).includes(search_value)));
    },
    all_visible_selected() {
      return this.filtered_cases.length > 0
        && this.filtered_cases.every((item) => this.selected_case_ids.includes(item.case_code));
    },
    selected_cases() {
      return selected_case_records(this.cases, this.selected_case_ids);
    },
    new_case_domain_options() {
      return this.case_types
        .map((entity_type) => ({
          id: Number(entity_type?.id),
          label: get_entity_type_name(entity_type) || `Entity type ${entity_type?.id}`,
        }))
        .filter((entity_type) => entity_type.id);
    },
    can_create_new_case() {
      const selected_case_type = this.case_types.find(
        (case_type) => Number(case_type?.id) === Number(this.manual_case.case_type)
      );
      return !validate_manual_case_registration(this.manual_case, selected_case_type)
        && !this.submitting_manual_case;
    },
    can_route_cases() {
      return can_route_case(this);
    },
    onboarding_preview_forms() {
      return Array.isArray(this.onboarding_preview?.forms)
        ? this.onboarding_preview.forms
        : [];
    },
    onboarding_completion() {
      return this.onboarding_status?.completion || {};
    },
    is_email_source() {
      return String(this.new_case_form.source_channel || '').toLowerCase() === 'email';
    },
    page_loading() {
      return this.loading_cases;
    },
    page_loading_message() {
      return 'Loading cases...';
    },
    page_loading_detail() {
      return 'Fetching the latest registered cases from the backend.';
    },
    modal_loading() {
      return this.show_new_case_modal && (this.onboarding_preview_loading || this.submitting_new_case);
    },
    modal_loading_message() {
      if (this.submitting_new_case) {
        return 'Creating case and running onboarding...';
      }

      return 'Preparing onboarding preview...';
    },
    modal_loading_detail() {
      if (this.submitting_new_case) {
        return 'Please wait while we create the case and start the workflow.';
      }

      return 'Loading the requestor experience for the selected domain.';
    },
    selected_assign_user() {
      return this.assignable_users.find((user) => String(user.user_id) === String(this.selected_assign_user_id)) || null;
    },
    can_submit_assign() {
      return Boolean(
        this.selected_case_ids.length
        && this.selected_assign_user
        && !this.submitting_assign_cases
      );
    },
  },
  watch: {
    should_open_new_case: {
      immediate: true,
      handler(should_open) {
        if (!should_open || this.show_new_case_modal || !this.case_types.length) {
          return;
        }

        this.open_new_case_modal();
      },
    },
    case_types: {
      deep: true,
      handler() {
        if (this.should_open_new_case && !this.show_new_case_modal && this.case_types.length) {
          this.open_new_case_modal();
        }
      },
    },
  },
  created() {
    this.load_company_cases();
  },
  methods: {
    ...company_cases_loading_methods,
    ...company_cases_creation_methods,
    async update_cases(profile_batches = []) {
      this.cases = this.normalize_cases(profile_batches);
    },
    set_filter(filter_key) {
      this.active_filter = filter_key;
      this.selected_case_ids = this.selected_case_ids.filter((case_code) =>
        this.filtered_cases.some((item) => item.case_code === case_code)
      );
    },
    toggle_case(case_code) {
      this.selected_case_ids = this.selected_case_ids.includes(case_code)
        ? this.selected_case_ids.filter((id) => id !== case_code)
        : [...this.selected_case_ids, case_code];
    },
    toggle_select_all(event) {
      const should_select = Boolean(event?.target?.checked);
      const visible_ids = this.filtered_cases.map((item) => item.case_code);

      this.selected_case_ids = should_select
        ? [...new Set([...this.selected_case_ids, ...visible_ids])]
        : this.selected_case_ids.filter((id) => !visible_ids.includes(id));
    },
    async load_workflow_status_filter_options() {
      this.workflow_status_filter_options = [];
    },
    open_new_case_modal() {
      this.manual_case = {
        ...manual_case_form(),
        case_type: String(this.new_case_domain_options[0]?.id || ''),
      };
      this.show_new_case_modal = true;
    },
    close_new_case_modal() {
      if (this.submitting_manual_case) return;
      this.dismiss_new_case_modal();
    },
    dismiss_new_case_modal() {
      this.show_new_case_modal = false;
      this.manual_case = manual_case_form();
    },
    async create_new_case() {
      const entity_type_id = Number(this.manual_case.case_type);
      const selected_case_type = this.case_types.find(
        (case_type) => Number(case_type?.id) === entity_type_id
      );
      const validation_error = validate_manual_case_registration(this.manual_case, selected_case_type);
      if (validation_error) {
        this.show_warning('Complete required fields', validation_error);
        return;
      }

      this.submitting_manual_case = true;

      try {
        const form_ids = await resolve_manual_case_form_ids({
          entity_type_id,
          project_id: this.project_id,
          fallback_form_ids: selected_case_type?.form_ids,
        });
        const payload = {
          entity_name: this.manual_case.description || this.manual_case.requester || 'Registered case',
          entity_type_id,
          form_ids,
          run_onboarding: true,
          source_channel: 'Portal',
          user_email: is_email(this.manual_case.contact) ? this.manual_case.contact : undefined,
          send_user_email: false,
          ...(this.project_id ? { project_id: Number(this.project_id) } : {}),
        };

        const create_data = await create_requestor_case(payload);
        const created_case = created_manual_case(create_data, entity_type_id);
        this.dismiss_new_case_modal();
        this.show_success('Case registered successfully.');
        if (created_case.application_id && created_case.case_code) {
          this.go_to_case_detail(created_case);
        } else {
          await this.load_company_cases();
        }
      } catch (error) {
        this.show_error(error?.response?.data?.message || error?.message || 'Failed to register case.');
      } finally {
        this.submitting_manual_case = false;
      }
    },
    async load_assignable_users() {
      this.loading_assignable_users = true;
      this.assign_error = '';

      try {
        const users = await fetch_reassignable_users(
          this.project_id ? { project_id: this.project_id } : {}
        );
        this.assignable_users = unique_by(
          users
            .map(assignable_case_handler)
            .filter(Boolean),
          (user) => String(user.user_id)
        ).sort((left, right) => left.name.localeCompare(right.name));

        if (!this.assignable_users.length) {
          this.assign_error = 'No panel case handlers are available for assignment.';
        }
      } catch (error) {
        this.assign_error = error?.response?.data?.message || error?.message || 'Failed to load panel case handlers.';
        this.assignable_users = [];
      } finally {
        this.loading_assignable_users = false;
      }
    },
    async open_assign_modal() {
      if (!this.selected_case_ids.length) {
        this.show_warning('Select a case', 'Select at least one registered case before assigning.');
        return;
      }

      this.show_assign_modal = true;
      this.selected_assign_user_id = '';
      this.assignable_users = [];
      await this.load_assignable_users();
    },
    close_assign_modal() {
      if (this.submitting_assign_cases) return;
      this.dismiss_assign_modal();
    },
    dismiss_assign_modal() {
      this.show_assign_modal = false;
      this.selected_assign_user_id = '';
      this.assign_error = '';
    },
    open_close_modal() {
      if (!this.selected_case_ids.length) {
        this.show_warning('Select a case', 'Select at least one registered case before closing.');
        return;
      }

      this.close_error = '';
      this.show_close_modal = true;
    },
    close_close_modal() {
      if (this.closing_cases) return;
      this.dismiss_close_modal();
    },
    dismiss_close_modal() {
      this.show_close_modal = false;
      this.close_error = '';
    },
    async assign_selected_cases() {
      if (!this.can_submit_assign) {
        this.assign_error = 'Select a case handler before assigning cases.';
        return;
      }

      this.submitting_assign_cases = true;
      this.assign_error = '';
      const loader = await this.show_loader('Assigning selected cases...');

      try {
        const selected_user = this.selected_assign_user;
        const results = await Promise.allSettled(
          this.selected_cases.map((case_item) =>
            assign_requestor_application_users({
              application_id: Number(case_item.application_id),
              assignments: [{
                user_id: Number(selected_user.user_id),
                role_id: Number(selected_user.role_id),
              }],
              note: `Assigned from Registered Cases to ${selected_user.name}.`,
            })
          )
        );

        const success_count = results.filter((result) => result.status === 'fulfilled').length;
        const failed = results.find((result) => result.status === 'rejected');

        if (failed) {
          throw failed.reason;
        }

        await this.load_company_cases();
        this.selected_case_ids = [];
        this.dismiss_assign_modal();
        this.show_success(
          success_count === 1
            ? 'Case assigned successfully.'
            : `${success_count} cases assigned successfully.`
        );
      } catch (error) {
        this.assign_error = error?.response?.data?.message || error?.message || 'Failed to assign selected cases.';
      } finally {
        this.submitting_assign_cases = false;
        this.close_loader(loader);
      }
    },
    async close_selected_cases() {
      if (this.closing_cases) {
        return;
      }
      if (!this.selected_case_ids.length) {
        this.show_warning('Select a case', 'Select at least one registered case before closing.');
        return;
      }

      this.closing_cases = true;
      this.close_error = '';
      const loader = await this.show_loader('Closing selected cases...');

      try {
        const results = await Promise.allSettled(
          this.selected_cases.map(async (case_item) => {
            const final_status = await final_workflow_status_for_case(case_item);

            if (!final_status?.label) {
              throw new Error(`No final workflow status is configured for ${case_item.case_code}.`);
            }

            await update_requestor_application_status({
              application_id: Number(case_item.application_id),
              entity_profile_id: Number(case_item.entity_profile_id) || null,
              status: final_status.label,
              note: 'Case closed from Registered Cases.',
              action_taken: 'Case closed',
            });

            return case_item.case_code;
          })
        );

        const success_count = results.filter((result) => result.status === 'fulfilled').length;
        const failed = results.find((result) => result.status === 'rejected');

        if (failed) {
          throw failed.reason;
        }

        await this.load_company_cases();
        this.selected_case_ids = [];
        this.dismiss_close_modal();
        this.show_success(
          success_count === 1
            ? 'Case closed successfully.'
            : `${success_count} cases closed successfully.`
        );
      } catch (error) {
        this.close_error = error?.response?.data?.message || error?.message || 'Failed to close selected cases.';
      } finally {
        this.closing_cases = false;
        this.close_loader(loader);
      }
    },
    go_to_case_detail(case_item) {
      const case_code = typeof case_item === 'string' ? case_item : case_item?.case_code;
      this.$router.push({
        name: 'ViewCaseDetails',
        params: { caseCode: case_code },
        query: case_detail_query(case_item),
      });
    },
  },
};
